import { observable } from "mobx";

import { SaveStatuses } from "./save-statuses";
import { axios } from "../../../../utils/axios";
import MetadataBase from "./metadata-base";
import { QuizObject } from "../quiz-object";

class QuizObjectFact {
    @observable _id: string = "";
    @observable valueType: string = "string";
    @observable slug: string = "";
    @observable name: string = "";
    @observable value: string = "";
    @observable order: number = 0;
    @observable status: SaveStatuses = SaveStatuses.UNCHANGED;
    @observable statusMessage: string = "";
    @observable deleted: boolean = false;
    rootStore: any;

    constructor(fact: any) {
        this._id = fact._id;
        if (fact.valueType) {
            this.valueType = fact.valueType;
        }
        this.slug = fact.slug;
        this.name = fact.name;
        this.value = fact.value;
        this.order = fact.order;
    }
}

async function asyncForEach(array: Array<any>, callback: Function) {
    for (let index = 0; index < array.length; index++) {
        await callback(array[index], index, array);
    }
}

class QuizObjectFactsMetadata extends MetadataBase {
    @observable toDelete: Array<number> = [];
    @observable toRestore: Array<number> = [];
    @observable toAdd: Array<number> = [];
    @observable toChange: Array<number> = [];
    @observable facts: Array<QuizObjectFact> = [];

    get status() {
        let status = undefined;
        this.facts.forEach((fact: QuizObjectFact) => {
            if (
                fact.status == SaveStatuses.SAVED ||
                fact.status == SaveStatuses.RESTORED ||
                fact.status == SaveStatuses.DELETED
            ) {
                status = SaveStatuses.SAVED;
            }
        });
        if (status) return status;

        this.facts.forEach((fact: QuizObjectFact) => {
            if (
                fact.status == SaveStatuses.CHANGED ||
                fact.status == SaveStatuses.DELETING ||
                fact.status == SaveStatuses.RESTORING ||
                fact.status == SaveStatuses.SAVING
            ) {
                status = SaveStatuses.SAVING;
            }
        });

        if (status) return status;

        return SaveStatuses.UNCHANGED;
    }

    getOrderFromID(id: string): number {
        let order = 0;
        this.facts.forEach((fact, i) => {
            if (fact._id == id) {
                order = i;
            }
        });
        return order;
    }

    pushToAdd(order: number) {
        this.toAdd.push(order);
        this.facts[order].status = SaveStatuses.CHANGED;
    }

    pushToChange(_id: string) {
        let order = this.getOrderFromID(_id);
        this.toChange.push(order);
        this.facts[order].status = SaveStatuses.CHANGED;
    }

    pushToDelete(_id: string) {
        let order = this.getOrderFromID(_id);
        this.toDelete.push(order);
        this.facts[order].status = SaveStatuses.CHANGED;
    }
    pushToRestore(_id: string) {
        let order = this.getOrderFromID(_id);
        this.toRestore.push(order);
        this.facts[order].status = SaveStatuses.CHANGED;
    }

    async deleteQueued(quizObject: any) {
        this.deduplicateFactsToDelete();
        await this.patchDeletedFacts(quizObject);
    }

    async restoreQueued(quizObject: any) {
        this.deduplicateFactsToRestore();
        await this.patchRestoredFacts(quizObject);
    }

    async changeQueued(quizObject: any) {
        this.deduplicateChangedFacts();
        await this.patchChangedFacts(quizObject);
    }

    async addQueued(quizObject: any) {
        this.deduplicateAddedFacts();
        await this.patchAddedFacts(quizObject);
    }

    deduplicateAddedFacts() {
        this.toAdd = this.deduplicate(this.toAdd);
    }

    deduplicateChangedFacts() {
        this.toChange = this.deduplicate(this.toChange);
    }

    deduplicateFactsToDelete() {
        this.toDelete = this.deduplicate(this.toDelete);
    }

    deduplicateFactsToRestore() {
        this.toRestore = this.deduplicate(this.toRestore);
    }

    async patchAddedFacts(quizObject: QuizObject) {
        const store = this;
        await asyncForEach(this.toAdd, async (order: any) => {
            if (!store.facts[order].name && store.facts[order].value) return;
            if (store.facts[order].status == SaveStatuses.SAVING) return;
            store.facts[order].status = SaveStatuses.SAVING;
            return axios()
                .patch(`/quiz_objects/${quizObject._id}`, {
                    fact: store.facts[order]
                })
                .then((res: any) => res.data)
                .then((res: any) => {
                    store.facts[order]._id = res.quiz_object.facts[order]._id;
                    store.facts[order].status = SaveStatuses.SAVED;
                })
                .catch((err: any) => {
                    store.facts[order].status = SaveStatuses.ERROR;
                    store.facts[order].statusMessage = err;
                });
        });
        store.toAdd = [];
    }

    async patchChangedFacts(quizObject: any) {
        let store = this;
        await asyncForEach(this.toChange, async (order: any) => {
            if (!this.facts[order].name && this.facts[order].value) return;
            if (this.facts[order].status == SaveStatuses.SAVING) return;
            this.facts[order].status = SaveStatuses.SAVING;

            return axios()
                .patch(`/quiz_objects/${quizObject._id}`, {
                    fact: store.facts[order]
                })
                .then((res: any) => res.data)
                .then(() => {
                    store.facts[order].status = SaveStatuses.SAVED;
                })
                .catch((err: any) => {
                    store.facts[order].status = SaveStatuses.ERROR;
                    store.facts[order].statusMessage = err;
                });
        });
        this.toChange = [];
    }

    async patchDeletedFacts(quizObject: any) {
        var deletedThisIteration: any[] = [];
        let store = this;
        await asyncForEach(this.toDelete, async (order: any) => {
            if (!this.facts[order].name && this.facts[order].value) return;
            if (this.facts[order].status == SaveStatuses.SAVING) return;
            this.facts[order].status = SaveStatuses.SAVING;

            deletedThisIteration.push(this.facts[order]._id);

            return axios()
                .patch(`/quiz_objects/${quizObject._id}`, {
                    deleteFact: store.facts[order]
                })
                .then(() => {
                    store.facts[order].status = SaveStatuses.DELETED;
                })
                .catch((err: any) => {
                    store.facts[order].status = SaveStatuses.ERROR;
                    store.facts[order].statusMessage = err;
                });
        });

        deletedThisIteration.forEach((deleted: any) => {
            this.toDelete.splice(this.toDelete.indexOf(deleted), 1);
        });
    }

    async patchRestoredFacts(quizObject: any) {
        let restoredThisIteration: any[] = [];
        let store = this;
        await asyncForEach(this.toRestore, async (order: any) => {
            restoredThisIteration.push(this.facts[order]._id);
            if (!this.facts[order]._id) return;
            if (this.facts[order].status == SaveStatuses.RESTORING) return;

            store.facts[order].status = SaveStatuses.RESTORING;
            return axios()
                .patch(`/quiz_objects/${quizObject._id}`, {
                    fact: store.facts[order]
                })
                .then((res: any) => {
                    return res.data;
                })
                .then((res: any) => {
                    store.facts[order]._id = res.quiz_object.facts[order]._id;
                    store.facts[order].status = SaveStatuses.RESTORED;
                })
                .catch((err: any) => {
                    store.facts[order].status = SaveStatuses.ERROR;
                    store.facts[order].statusMessage = err;
                });
        });
        restoredThisIteration.forEach((deleted: any) => {
            this.toRestore.splice(this.toRestore.indexOf(deleted), 1);
        });
    }

    constructor() {
        super();
    }
}

export { QuizObjectFact, QuizObjectFactsMetadata };
